"use client";
import React, {
  CSSProperties,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { Drawer as AntdDrawer } from "antd";
import { ospThemes } from "@ospweb/sdk-ui";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { cn } from "utils";
import { DRAG_CLOSE_ANIM, useDraggable } from "./Drawer/useDraggable";

/** 拖拽松手后回位（与 EdgenDrawer 一致） */
const DRAG_RELEASE_EASING = "cubic-bezier(0.175, 0.885, 0.32, 1.275)";
const DRAG_RELEASE_DURATION_S = 0.42;

type DrawerH5SwipeContextValue = {
  handleMouseDown: (e: React.MouseEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleDragBarClick: () => void;
};

const DrawerH5SwipeContext = createContext<DrawerH5SwipeContextValue | null>(
  null,
);

/** H5 bottom sheet 内自定义 drag handle 时绑定下拉关闭手势 */
export function useDrawerH5Swipe() {
  return useContext(DrawerH5SwipeContext);
}

export interface DrawerH5Props {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  zIndex?: number;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
  /** 用于 css 样式匹配 */
  domClassName?: string;
  className?: string;
  closeSize?: number;
  /** 列表顶下拉关闭；默认 true，不改变静态 UI（无拖条） */
  swipeToClose?: boolean;
  /** 下拉关闭阈值（px），默认 200 */
  dragThreshold?: number;
  /** @default true */
  maskClosable?: boolean;
}

export const DrawerH5: React.FC<DrawerH5Props> = ({
  open,
  onClose,
  title,
  children,
  zIndex = 1000,
  containerStyle,
  containerClassName,
  contentStyle,
  wrapperStyle,
  domClassName = "",
  className = "",
  closeSize = 16,
  swipeToClose = false,
  dragThreshold = 200,
  maskClosable = true,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollRootVersion, setScrollRootVersion] = useState(0);

  const assignScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el;
    setScrollRootVersion((v) => v + 1);
  }, []);

  const listPullDownFromScrollRoot = useMemo(
    () =>
      swipeToClose
        ? {
            getScrollRoot: () => scrollRef.current,
            dependencyKey: scrollRootVersion,
          }
        : undefined,
    [swipeToClose, scrollRootVersion],
  );

  const {
    isDragging,
    isDragCloseAnimating,
    dragDistance,
    handleMouseDown,
    handleTouchStart,
    handleDragBarClick,
  } = useDraggable({
    enabled: swipeToClose,
    onClose,
    threshold: dragThreshold,
    isOpen: open,
    listPullDownFromScrollRoot,
  });

  const swipeContextValue = useMemo<DrawerH5SwipeContextValue | null>(
    () =>
      swipeToClose
        ? { handleMouseDown, handleTouchStart, handleDragBarClick }
        : null,
    [handleDragBarClick, handleMouseDown, handleTouchStart, swipeToClose],
  );

  const shouldApplyDragTransform =
    swipeToClose && (isDragging || isDragCloseAnimating || dragDistance > 0);

  const drawerHeight = contentStyle?.height || "auto";

  const mergedWrapperStyle: CSSProperties = {
    ...wrapperStyle,
    ...(shouldApplyDragTransform && {
      transform: `translateY(${dragDistance}px)`,
      transition: isDragging
        ? "none"
        : isDragCloseAnimating
          ? `transform ${DRAG_CLOSE_ANIM.durationS}s ${DRAG_CLOSE_ANIM.easing}`
          : `transform ${DRAG_RELEASE_DURATION_S}s ${DRAG_RELEASE_EASING}`,
    }),
  };

  return (
    <AntdDrawer
      open={open}
      onClose={onClose}
      placement="bottom"
      height={drawerHeight}
      closable={false}
      destroyOnClose={false}
      maskClosable={maskClosable}
      zIndex={zIndex}
      rootClassName={domClassName}
      className={cn(
        "[--content-max-height:calc(100vh-60px)] [@media(max-height:600px)]:[--content-max-height:calc(100vh-40px)]",
        className,
      )}
      styles={{
        header: { display: "none" },
        wrapper: mergedWrapperStyle,
        body: {
          padding: "0",
          background: ospThemes.colors.background_solid_secondary,
        },
        content: {
          borderRadius: "16px 16px 0 0",
          maxHeight: contentStyle?.height || "var(--content-max-height)",
          background: ospThemes.colors.background_solid_secondary,
          ...contentStyle,
        },
        mask: {
          backgroundColor: ospThemes.colors.background_clearcover,
        },
      }}
      title={title}
    >
      <DrawerH5SwipeContext.Provider value={swipeContextValue}>
        {title && (
          <div
            data-openreplay-label="misc_drawerh5_close"
            className="p-[6px] absolute top-2 right-2"
            onClick={onClose}
          >
            <OspIcons
              name="close"
              className="text-content_tertiary"
              size={closeSize}
            />
          </div>
        )}
        {title && (
          <div className="headline_regular_s text-content_primary mx-4 mt-4">
            {title}
          </div>
        )}
        <div
          ref={assignScrollRef}
          style={{
            overflowY: "auto",
            maxHeight:
              containerStyle?.height ||
              containerStyle?.maxHeight ||
              "var(--container-max-height)",
            ...containerStyle,
          }}
          className={cn(
            "flex-1 [--container-max-height:calc(100vh-120px)] [@media(max-height:600px)]:[--container-max-height:calc(100vh-40px)]",
            containerClassName,
          )}
        >
          {children}
        </div>
      </DrawerH5SwipeContext.Provider>
    </AntdDrawer>
  );
};
